import React, { useEffect, useState } from 'react';
import { Sword, Shield, Crown, Scroll, Sparkles } from 'lucide-react';

export function MedievalIntro({ onComplete }: { onComplete: () => void }) {
  const [step, setStep] = useState(0);
  
  useEffect(() => {
    const sequence = [
      () => setStep(1), // Initial fade in
      () => setStep(2), // Show "Hear Ye, Hear Ye"
      () => setStep(3), // Show "Welcome To"
      () => setStep(4), // Show "Arcadia"
      () => {
        setStep(5);     // Final transition
        setTimeout(onComplete, 1000);
      }
    ];

    // Longer delays for better readability
    const delays = [0, 2000, 4000, 6000, 8000];
    sequence.forEach((action, index) => {
      setTimeout(action, delays[index]);
    });
  }, [onComplete]);

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-gray-900 via-amber-900 to-gray-900 flex items-center justify-center overflow-hidden">
      <div className={`transition-all duration-1000 ${step >= 5 ? 'opacity-0 scale-110' : 'opacity-100'}`}>
        {/* Magical runes circle */}
        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-1000 
          ${step >= 2 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="relative w-96 h-96">
            <div className="absolute inset-0 border-2 border-amber-500/30 rounded-full animate-spin-slow" />
            <div className="absolute inset-0 border-2 border-cyan-500/30 rounded-full animate-spin-reverse" />
            {Array.from({ length: 8 }).map((_, i) => (
              <Sparkles
                key={i}
                className={`absolute w-6 h-6 text-yellow-500/50
                  ${i % 2 === 0 ? 'animate-pulse' : 'animate-pulse-slow'}`}
                style={{
                  top: `${50 + 45 * Math.sin((i * Math.PI) / 4)}%`,
                  left: `${50 + 45 * Math.cos((i * Math.PI) / 4)}%`,
                  transform: 'translate(-50%, -50%)'
                }}
              />
            ))}
          </div>
        </div>

        <div className={`text-center relative z-10 ${step >= 1 ? 'animate-fade-in' : 'opacity-0'}`}>
          {/* Icons */}
          <div className="flex items-center justify-center mb-8 space-x-6">
            <Shield className="w-12 h-12 text-yellow-600 animate-pulse" />
            <Crown className="w-12 h-12 text-amber-500 animate-pulse-slow" />
            <Sword className="w-12 h-12 text-cyan-500 animate-pulse" />
          </div>
          
          {/* Herald's Announcement */}
          <div className={`space-y-4 transition-all duration-500
            ${step >= 2 ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
            <p className="text-yellow-600 font-serif text-2xl tracking-widest">HEAR YE, HEAR YE</p>
          </div>

          {/* Welcome Message */}
          <div className={`mt-6 space-y-4 transition-all duration-500
            ${step >= 3 ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
            <div className="flex items-center justify-center gap-2">
              <Scroll className="w-4 h-4 text-amber-500" />
              <p className="text-amber-200 font-serif text-lg">WELCOME TO</p>
              <Scroll className="w-4 h-4 text-amber-500" />
            </div>
          </div>

          {/* Main Title */}
          <h1 className={`text-5xl font-bold mt-8 font-serif tracking-wider
            ${step >= 4 ? 'opacity-100 transform scale-100' : 'opacity-0 transform scale-90'}
            transition-all duration-700`}>
            <span className="bg-gradient-to-r from-yellow-600 via-amber-500 to-yellow-600 text-transparent bg-clip-text">
              Solstice
            </span>
          </h1>
        </div>
      </div>
    </div>
  );
}